import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import db from '../../db/db';
import TicketCorte from './TicketCorte';

const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Cortes = () => {
    const navigate = useNavigate();

    const [cortes, setCortes]           = useState([]);
    const [empleados, setEmpleados]     = useState([]);
    const [sucursalNombre, setSucursalNombre] = useState('');
    const [ticketReimprimir, setTicketReimprimir] = useState(null);

    const cargar = useCallback(async () => {
        const sesion = await db.sesion.toArray();
        const sucursalId = sesion[0]?.sucursal?._id || sesion[0]?.sucursal;
        const [cs, emps, sucursales] = await Promise.all([
            db.cortes.filter(c => String(c.sucursal?._id || c.sucursal) === String(sucursalId)).toArray(),
            db.empleados.toArray(),
            db.sucursales.toArray(),
        ]);
        const sucReg = sucursales.find(s => String(s._id || s.id) === String(sucursalId));
        setSucursalNombre(sucReg?.nombre || '');
        setEmpleados(emps);
        setCortes(cs.sort((a, b) => new Date(b.fecha_inicio || b.fecha) - new Date(a.fecha_inicio || a.fecha)));
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const resolverEmpleado = (id) => {
        if (!id) return '—';
        const emp = empleados.find(e => e._id === id || String(e.id) === String(id));
        if (!emp) return '—';
        return `${emp.nombre} ${emp.apellido_paterno || ''}`.trim();
    };

    const handleReimprimir = (row) => {
        setTicketReimprimir({
            _corteId:        row.id,
            folio:           row.folio,
            fecha:           row.fecha_inicio || row.fecha,
            responsable:     resolverEmpleado(row.responsable),
            sucursal:        sucursalNombre,
            efectivoInicial: row.total_inicial    || 0,
            totalVentas:     row.total_ventas     || 0,
            totalVueltas:    row.total_vueltas    || 0,
            totalGastos:     row.total_gastos     || 0,
            totalFinal:      row.total_final      || 0,
            metodos:         (row.desglose_metodos || []).map(m => {
                const totEntry = (row.totales || []).find(t => t.metodo === m._id);
                const totalReal = totEntry?.total > 0 ? totEntry.total : null;
                return { ...m, totalReal };
            }),
            conosVendidos:   row.total_conos_vendidos || 0,
            conosIniciales:  row.conos_inicial    || 0,
            conosTotales:    (row.conos_inicial   || 0) + (row.total_conos_ingresados || 0),
            conosFinales:    row.conos_final      ?? 0,
            tiposConos:      row.tipos_conos      || [],
            evento:          row.evento_nombre    || '',
        });
    };

    const columns = [
        {
            name: 'Folio',
            selector: row => row.folio,
            sortable: true,
            grow: 0.5,
            cell: row => (
                <span style={{ color: '#F5C800', fontWeight: 700, fontFamily: 'monospace' }}>
                    {row.folio || '—'}
                </span>
            ),
        },
        {
            name: 'Responsable',
            selector: row => resolverEmpleado(row.responsable),
            sortable: true,
            grow: 1,
            cell: row => <span style={{ color: '#C0C0C0' }}>{resolverEmpleado(row.responsable)}</span>,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            grow: 0.7,
            cell: row => row.status ? (
                <span style={badges.finalizado}>Finalizado</span>
            ) : (
                <span style={badges.pendiente}>Pendiente</span>
            ),
        },
        {
            name: 'Fecha',
            selector: row => row.fecha_inicio || row.fecha,
            sortable: true,
            grow: 1,
            cell: row => {
                const d = new Date(row.fecha_inicio || row.fecha);
                return (
                    <span style={{ color: '#C0C0C0', fontSize: '0.85rem' }}>
                        {d.toLocaleDateString('es-MX')} {d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                );
            },
        },
        {
            name: 'Inicial',
            selector: row => row.total_inicial,
            sortable: true,
            grow: 0.7,
            cell: row => <span style={{ color: '#F0F0F0', fontWeight: 700 }}>${fmt(row.total_inicial)}</span>,
        },
        {
            name: 'Ventas',
            selector: row => row.total_ventas,
            sortable: true,
            grow: 0.7,
            cell: row => <span style={{ color: '#48BB78', fontWeight: 600 }}>${fmt(row.total_ventas)}</span>,
        },
        {
            name: 'V. amb.',
            selector: row => row.total_vueltas,
            sortable: true,
            grow: 0.7,
            cell: row => <span style={{ color: '#63B3ED', fontWeight: 600 }}>${fmt(row.total_vueltas)}</span>,
        },
        {
            name: 'Gastos',
            selector: row => row.total_gastos,
            sortable: true,
            grow: 0.7,
            cell: row => <span style={{ color: '#ED8936', fontWeight: 600 }}>${fmt(row.total_gastos)}</span>,
        },
        {
            name: '',
            width: '100px',
            cell: row => (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                    {!row.status && (
                        <button
                            style={styles.btnIr}
                            title="Continuar corte"
                            onClick={() => navigate('/corte')}
                        >
                            <i className="fas fa-arrow-right"></i>
                        </button>
                    )}
                    {row.status && (
                        <button
                            style={styles.btnPrint}
                            title="Reimprimir ticket"
                            onClick={() => handleReimprimir(row)}
                        >
                            <i className="fas fa-print"></i>
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
        {ticketReimprimir && (
            <TicketCorte
                data={ticketReimprimir}
                onImprimir={async () => {
                    await db.cortes.update(ticketReimprimir._corteId, { ticket_impreso: true });
                }}
                onCerrar={() => setTicketReimprimir(null)}
            />
        )}
        <div style={styles.page}>
            <div style={styles.header}>
                <i className="fas fa-cash-register me-3" style={{ color: '#F5C800', fontSize: '1.3rem' }}></i>
                <h4 style={styles.titulo}>Historial de cortes</h4>
            </div>

            <div style={styles.tableWrap}>
                <DataTable
                    columns={columns}
                    data={cortes}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                    noDataComponent={
                        <div style={styles.vacio}>
                            <i className="fas fa-cash-register" style={{ fontSize: '2rem', color: '#333', marginBottom: '0.75rem' }}></i>
                            <p style={{ color: '#888', margin: 0 }}>No hay cortes registrados</p>
                        </div>
                    }
                    customStyles={tableStyles}
                    defaultSortFieldId={4}
                    defaultSortAsc={false}
                    dense
                />
            </div>
        </div>
        </>
    );
};

const badges = {
    finalizado: {
        background: 'rgba(72,187,120,0.1)',
        color: '#48BB78',
        border: '1px solid rgba(72,187,120,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    pendiente: {
        background: 'rgba(229,62,62,0.1)',
        color: '#E53E3E',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.65rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
};

const styles = {
    page: {
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
    },
    tableWrap: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    vacio: {
        padding: '3rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    btnIr: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#555',
        fontSize: '0.85rem',
        cursor: 'pointer',
    },
    btnPrint: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#F5C800',
        fontSize: '0.85rem',
        cursor: 'pointer',
    },
};

const tableStyles = {
    table: { style: { background: '#1A1A1A' } },
    headRow: { style: { background: '#111111', borderBottomColor: '#2E2E2E' } },
    headCells: { style: { color: '#888888', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' } },
    rows: { style: { background: '#1A1A1A', borderBottomColor: '#222222', minHeight: '52px', '&:hover': { background: '#1F1F1F' } } },
    pagination: { style: { background: '#111111', borderTopColor: '#2E2E2E', color: '#888' } },
    noData: { style: { background: '#1A1A1A' } },
};

export default Cortes;
